import { Injectable } from '@angular/core';
import type { MsalService } from '@azure/msal-angular';
import { Store } from '@ngrx/store';
import { Observable, from } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { ManageIdbService } from 'src/app/idb/manage-idb.service';
import { resetLogin } from 'src/app/store/login/login.actions';
import { ArkikAlertService } from '@shared/components/arkik-alert/arkik-alert.service';
import { CatalogSessionService } from './catalog-session.service';

@Injectable({ providedIn: 'root' })
export class CatalogLogoutService {
  private pending?: Promise<void>;
  private generation?: number;

  constructor(
    private session: CatalogSessionService,
    private idb: ManageIdbService,
    private store: Store,
    private alert: ArkikAlertService,
  ) {}

  start(): Promise<void> {
    this.session.beginLogout();
    if (!this.pending || this.generation !== this.session.logoutGeneration) {
      this.generation = this.session.logoutGeneration;
      this.store.dispatch(resetLogin());
      this.pending = this.idb.cleanupInvalidCache().catch((error: unknown) => {
        console.error('Logout catalog cache cleanup/invalidation failed', error);
        // Without a durable marker, navigation/reload could expose the old cache.
        // Fail closed instead of redirecting when cleanup AND marker persistence fail.
        try {
          this.session.assertDurableInvalidation();
        } catch (markerError) {
          this.alert.error('Logout blocked: cache cleanup and persistent invalidation both failed.', 0, true);
          throw markerError;
        }
        this.alert.error('Catalog cache cleanup failed. Cached data remains blocked until cleanup succeeds.', 0, true);
      });
    }
    return this.pending;
  }
}

type LogoutClient = Pick<MsalService, 'logout' | 'logoutRedirect' | 'logoutPopup'>;

// MSAL methods are eager: existing callers intentionally do not subscribe.
// Keep that contract, but do not invoke MSAL/navigation before the cache barrier.
export function protectCatalogLogout<T extends LogoutClient>(client: T, lifecycle: CatalogLogoutService): T {
  const logout = client.logout.bind(client);
  const redirect = client.logoutRedirect.bind(client);
  const popup = client.logoutPopup.bind(client);
  const afterCleanup = (invoke: () => Observable<void>): Observable<void> => {
    const pending = lifecycle.start().then(invoke);
    pending.catch((error: unknown) =>
      console.error('Logout redirect blocked: cache invalidation is not durable', error),
    );
    return from(pending).pipe(switchMap((result) => result));
  };
  client.logout = (...args) => afterCleanup(() => logout(...args));
  client.logoutRedirect = (...args) => afterCleanup(() => redirect(...args));
  client.logoutPopup = (...args) => afterCleanup(() => popup(...args));
  return client;
}
